export type DataCenter = 'ovh-ca' | 'aws-ca' | 'do-sfo'

export const DC_CONFIG: Record<DataCenter, {
  apiUrl: string
  mcpUrl: string
  label: string
  region: string
  githubConnectUrl: string
  /** VeilStream read gateway host suffix (slug is prefixed). */
  psqlProxyDomain: string
  /** VeilStream write-approval gateway host suffix (slug is prefixed). */
  psqlWriteProxyDomain: string
  /** iOS phone preview host suffix (slug is prefixed); carries the DC name like env-*. */
  phoneDomain: string
  /** Fixed outbound IPs for hosted DB introspection — allowlist on customer firewalls. */
  egressIps: readonly string[]
  /** Hostname customers CNAME their custom domains to (cluster Traefik / NLB). */
  customDomainCnameTarget: string
  /** Retired DC — shown on login but not selectable. */
  sunset?: boolean
}> = {
  'ovh-ca': {
    apiUrl: 'https://api-ca.veilstream.com',
    mcpUrl: 'https://mcp-ca.veilstream.com/mcp',
    label: 'Beauharnois',
    region: 'OVH Public Cloud — Beauharnois, Quebec',
    githubConnectUrl: 'https://github.com/apps/veilstream-beauharnois/installations/new',
    psqlProxyDomain: 'psql-ca.veilstream.com',
    psqlWriteProxyDomain: 'psql-write-ca.veilstream.com',
    phoneDomain: 'phone-ca.veilstreamapp.com',
    egressIps: [],
    customDomainCnameTarget: 'custom.veilstream.com',
  },
  'aws-ca': {
    apiUrl: 'https://api.veilstream.com',
    mcpUrl: 'https://mcp.veilstream.com/mcp',
    label: 'Montreal',
    region: 'AWS ca-central-1 — Montreal',
    githubConnectUrl: 'https://github.com/apps/veilstream/installations/new',
    psqlProxyDomain: 'psql.veilstream.com',
    psqlWriteProxyDomain: 'psql-write.veilstream.com',
    phoneDomain: 'phone.veilstreamapp.com',
    egressIps: [],
    customDomainCnameTarget: 'custom.veilstream.com',
    sunset: true,
  },
  'do-sfo': {
    apiUrl: 'https://api-sfo.veilstream.com',
    mcpUrl: 'https://mcp-sfo.veilstream.com/mcp',
    label: 'San Francisco',
    region: 'DigitalOcean sfo3 — San Francisco',
    githubConnectUrl: 'https://github.com/apps/veilstream-sfo/installations/new',
    psqlProxyDomain: 'psql-sfo.veilstream.com',
    psqlWriteProxyDomain: 'psql-write-sfo.veilstream.com',
    phoneDomain: 'phone-sfo.veilstreamapp.com',
    // NAT workers + psql-gateway (ingress node). Keep in sync with infrastructure/do-sfo-learnings.md.
    egressIps: ['209.38.172.64', '64.23.139.70'],
    customDomainCnameTarget: 'custom-sfo.veilstream.com',
  },
}

const DC_STORAGE_KEY = 'veilstream_datacenter'

/** Fallback when hostname does not imply a data center (e.g. local dev). */
export const DEFAULT_DATA_CENTER: DataCenter = 'ovh-ca'

/** Default DC for new sessions. */
export function getDefaultDataCenter(): DataCenter {
  return DEFAULT_DATA_CENTER
}

export function isDataCenterSunset(dc: DataCenter): boolean {
  return DC_CONFIG[dc].sunset === true
}

/** Map retired DCs to the current default for stored logins and deep links. */
export function resolveSelectableDC(dc: DataCenter): DataCenter {
  return isDataCenterSunset(dc) ? DEFAULT_DATA_CENTER : dc
}

function isDataCenter(value: string | null): value is DataCenter {
  return value === 'ovh-ca' || value === 'aws-ca' || value === 'do-sfo'
}

export function getStoredDC(): DataCenter | null {
  const raw = localStorage.getItem(DC_STORAGE_KEY)
  if (!isDataCenter(raw)) return null
  return resolveSelectableDC(raw)
}

/** Map `?region=` values (city name, slug, or DC id) to a data center. */
export function dataCenterFromRegionParam(region: string): DataCenter | null {
  const key = region.trim().toLowerCase().replace(/[\s_]+/g, '-')
  if (!key) return null

  const aliases: Record<string, DataCenter> = {
    'ovh-ca': 'ovh-ca',
    ovh: 'ovh-ca',
    beauharnois: 'ovh-ca',
    'aws-ca': 'aws-ca',
    aws: 'aws-ca',
    montreal: 'aws-ca',
    'ca-central-1': 'aws-ca',
    'do-sfo': 'do-sfo',
    do: 'do-sfo',
    sfo: 'do-sfo',
    sfo3: 'do-sfo',
    'san-francisco': 'do-sfo',
    sanfrancisco: 'do-sfo',
    sf: 'do-sfo',
  }

  return aliases[key] ?? null
}

function dcFromCurrentUrl(): DataCenter | null {
  if (typeof window === 'undefined') return null
  const region = new URLSearchParams(window.location.search).get('region')
  if (!region?.trim()) return null
  return dataCenterFromRegionParam(region)
}

export function getSelectedDC(): DataCenter {
  const fromUrl = dcFromCurrentUrl()
  if (fromUrl) return resolveSelectableDC(fromUrl)
  return getStoredDC() ?? getDefaultDataCenter()
}

export function setSelectedDC(dc: DataCenter): void {
  localStorage.setItem(DC_STORAGE_KEY, dc)
}

const trimTrailingSlash = (s: string) => s.replace(/\/+$/, '')

function apiUrlFromBuildEnv(): string {
  const backend = typeof import.meta.env.VITE_BACKEND_URL === 'string' ? import.meta.env.VITE_BACKEND_URL.trim() : ''
  if (backend) return trimTrailingSlash(backend)
  const base = typeof import.meta.env.VITE_API_BASE_URL === 'string' ? import.meta.env.VITE_API_BASE_URL.trim() : ''
  if (base) return trimTrailingSlash(base)
  return ''
}

/** Dev static hosting and local Vite — not the OVH prod console host. */
function apiUrlFromHostname(): string {
  if (typeof window === 'undefined') return ''
  const host = window.location.hostname
  if (
    host === 'app.veilstreamdev.com' ||
    host.endsWith('.veilstreamdev.com') ||
    host === 'localhost' ||
    host === '127.0.0.1'
  ) {
    return 'https://api.veilstreamdev.com'
  }
  return ''
}

/** Multi-DC console must honor the login DC picker — never a single build-time API URL. */
function isMultiDcConsoleHost(): boolean {
  return typeof window !== 'undefined' && window.location.hostname === 'console.veilstream.com'
}

/**
 * Resolve the API root for the active environment / data center.
 * Prefer {@link apiUrl} / {@link apiClient} / {@link apiFetch} for requests so call
 * sites stay path-only (`/api/...`). Use this for customer-facing snippets that must
 * display the absolute API host.
 */
export function getApiUrl(): string {
  if (!isMultiDcConsoleHost()) {
    const fromBuild = apiUrlFromBuildEnv()
    if (fromBuild) return fromBuild
  }
  const fromHost = apiUrlFromHostname()
  if (fromHost) return fromHost
  return DC_CONFIG[getSelectedDC()].apiUrl
}

export function getWsUrl(): string {
  return getApiUrl().replace(/^http/, 'ws').replace(/^https/, 'wss')
}

export function getGithubCallbackUrl(): string {
  return `${getApiUrl()}/api/auth/github/callback`
}

export function getGithubConnectUrl(): string {
  return DC_CONFIG[getSelectedDC()].githubConnectUrl
}

const DEV_MCP_URL = 'https://mcp.veilstreamdev.com/mcp'

export function getMcpUrl(): string {
  const fromHost = apiUrlFromHostname()
  if (fromHost) return DEV_MCP_URL
  return DC_CONFIG[getSelectedDC()].mcpUrl
}

/** Public read-gateway hostname suffix for the active data center (e.g. psql-sfo.veilstream.com). */
export function getPsqlProxyDomain(): string {
  const api = getApiUrl()
  if (api.includes('veilstreamdev.com') || api.includes('localhost')) {
    return 'psql.veilstreamdev.com'
  }
  return DC_CONFIG[getSelectedDC()].psqlProxyDomain
}

/** Write-approval gateway hostname suffix (e.g. psql-write-sfo.veilstream.com). */
export function getPsqlWriteProxyDomain(): string {
  const api = getApiUrl()
  if (api.includes('veilstreamdev.com') || api.includes('localhost')) {
    return 'psql-write.veilstreamdev.com'
  }
  return DC_CONFIG[getSelectedDC()].psqlWriteProxyDomain
}

/** Hostname customers should CNAME custom domains to for the active data center. */
export function getCustomDomainCnameTarget(): string {
  const api = getApiUrl()
  if (api.includes('veilstreamdev.com') || api.includes('localhost')) {
    return 'custom.veilstreamdev.com'
  }
  return DC_CONFIG[getSelectedDC()].customDomainCnameTarget
}

// Static snapshots — use the getter where the DC can change during a session.
export const GITHUB_APP_NAME = 'veilstream'
export const GITHUB_APP_URL = getGithubConnectUrl()
export const GITHUB_APP_INSTALL_URL = getGithubConnectUrl()

// WebSocket — resolve dynamically at call sites with getWsUrl()
export const WS_URL = getWsUrl()
